import { z } from "zod";
import { contactGroupSelectionFieldsSchema } from "./contact-group-selection.js";
import { birthdaySchema } from "./date-only.js";
import { contactBulkMatchingQuerySchema } from "./contact-list-query.js";
import { apiIntIdSchema } from "./ids.js";
import { contactPlatformProfileSummaryDtoSchema } from "./contact-state.js";
export const contactGroupReferenceSchema = z.object({
    id: apiIntIdSchema,
    name: z.string(),
});
export const contactGroupSummarySchema = contactGroupReferenceSchema.extend({
    memberCount: z.number().int().nonnegative(),
});
export const contactListItemSchema = z.object({
    id: apiIntIdSchema,
    displayName: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    normalizedPhoneNumber: z.string().nullable(),
    /** Calendar date `YYYY-MM-DD`, never a timestamp. */
    birthday: z.string().nullable(),
    groups: z.array(contactGroupReferenceSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
    platformProfileCount: z.number().int().nonnegative(),
    linkedPlatformTypes: z.array(z.enum(["WHATSAPP", "FACEBOOK", "INSTAGRAM"])),
    whatsAppPhoneLocked: z.boolean(),
});
export const contactListResponseSchema = z.object({
    items: z.array(contactListItemSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalCount: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
export const contactDetailPlatformProfileSchema = z.object({
    id: apiIntIdSchema,
    platformType: z.enum(["WHATSAPP", "FACEBOOK", "INSTAGRAM"]),
    platformDisplayName: z.string().nullable(),
    platformUsername: z.string().nullable(),
    normalizedPhoneNumber: z.string().nullable(),
    externalProfileId: z.string(),
    conversationId: apiIntIdSchema.nullable(),
    platformConnectionStatus: z.enum(["PENDING", "ACTIVE", "DISCONNECTED"]),
});
export const contactDetailSchema = z.object({
    id: apiIntIdSchema,
    displayName: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    normalizedPhoneNumber: z.string().nullable(),
    /** Calendar date `YYYY-MM-DD`, never a timestamp. */
    birthday: z.string().nullable(),
    groups: z.array(contactGroupReferenceSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().nullable(),
    platformProfiles: z.array(contactDetailPlatformProfileSchema),
    linkedPlatformTypes: z.array(z.enum(["WHATSAPP", "FACEBOOK", "INSTAGRAM"])),
    whatsAppPhoneLocked: z.boolean(),
});
const optionalEmailSchema = z
    .string()
    .trim()
    .email()
    .optional()
    .or(z.literal("").transform(() => undefined));
const optionalPhoneSchema = z
    .string()
    .trim()
    .optional()
    .or(z.literal("").transform(() => undefined));
/** Shared optional CRM write fields for create and add-as-contact flows. */
export const contactCrmWriteFieldsSchema = z.object({
    firstName: z.string().trim().max(100).optional(),
    lastName: z.string().trim().max(100).optional(),
    displayName: z.string().trim().min(1).max(200).optional(),
    email: optionalEmailSchema,
    phone: optionalPhoneSchema,
    /** ISO 3166-1 alpha-2 when phone lacks leading `+`. */
    phoneCountryIso: z.string().trim().length(2).optional(),
    /** `YYYY-MM-DD`. Omitted or null means no birthday. */
    birthday: birthdaySchema.nullable().optional(),
});
export const createContactRequestSchema = contactCrmWriteFieldsSchema.extend({
    ...contactGroupSelectionFieldsSchema,
});
export const updateContactRequestSchema = z.object({
    firstName: z.string().trim().nullable().optional(),
    lastName: z.string().trim().nullable().optional(),
    displayName: z.string().trim().optional(),
    email: optionalEmailSchema.nullable(),
    phone: optionalPhoneSchema.nullable(),
    phoneCountryIso: z.string().trim().length(2).optional(),
    /** Omitted leaves the birthday unchanged; null clears it. */
    birthday: birthdaySchema.nullable().optional(),
    groupIds: z.array(apiIntIdSchema).optional(),
    newGroupNames: contactGroupSelectionFieldsSchema.newGroupNames,
});
export const createContactResponseSchema = z.object({
    contact: contactDetailSchema,
});
export const updateContactResponseSchema = z.object({
    contact: contactDetailSchema,
});
export const contactGroupListResponseSchema = z.object({
    items: z.array(contactGroupSummarySchema),
});
export const createContactGroupRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
});
export const updateContactGroupRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
});
export const contactImportResultSchema = z.object({
    created: z.number().int().nonnegative(),
    restored: z.number().int().nonnegative().optional(),
    skipped: z.number().int().nonnegative(),
    errors: z.array(z.object({
        row: z.number().int().positive(),
        message: z.string(),
        code: z.string().optional(),
    })),
});
export const contactExportRequestSchema = z.object({
    contactIds: z.array(apiIntIdSchema).optional(),
    query: z.record(z.string(), z.string()).optional(),
});
/** Internal batch size for semantic bulk operations. Not a product cap. */
export const CONTACT_BULK_ID_BATCH_SIZE = 1000;
const contactBulkIdArraySchema = z.array(apiIntIdSchema).max(CONTACT_BULK_ID_BATCH_SIZE);
export const contactBulkSelectionSchema = z.discriminatedUnion("mode", [
    z.object({
        mode: z.literal("ids"),
        ids: z.array(apiIntIdSchema).min(1).max(CONTACT_BULK_ID_BATCH_SIZE),
    }),
    z.object({
        mode: z.literal("matching_query"),
        query: contactBulkMatchingQuerySchema,
        excludedIds: contactBulkIdArraySchema.optional(),
    }),
    z.object({
        mode: z.literal("all_active"),
        excludedIds: contactBulkIdArraySchema.optional(),
    }),
]);
function requireContactIdsOrSelection(value, ctx) {
    const hasIds = (value.contactIds?.length ?? 0) > 0;
    const hasSelection = value.selection != null;
    if (hasIds === hasSelection) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Provide either contactIds or selection",
            path: hasSelection ? ["selection"] : ["contactIds"],
        });
    }
}
export function resolveContactBulkSelection(input) {
    if (input.selection)
        return input.selection;
    return { mode: "ids", ids: [...(input.contactIds ?? [])] };
}
export const contactBulkDeleteRequestSchema = z
    .object({
    contactIds: z.array(apiIntIdSchema).min(1).max(CONTACT_BULK_ID_BATCH_SIZE).optional(),
    selection: contactBulkSelectionSchema.optional(),
})
    .superRefine(requireContactIdsOrSelection);
export const contactBulkDeleteResponseSchema = z.object({
    deleted: z.number().int().nonnegative(),
});
export const contactBulkGroupsModeSchema = z.enum(["add", "remove", "replace", "clear"]);
export const contactBulkGroupsRequestSchema = z
    .object({
    contactIds: z.array(apiIntIdSchema).min(1).max(CONTACT_BULK_ID_BATCH_SIZE).optional(),
    selection: contactBulkSelectionSchema.optional(),
    mode: contactBulkGroupsModeSchema,
    groupIds: z.array(apiIntIdSchema).optional(),
    newGroupNames: z.array(z.string().trim().min(1).max(120)).optional(),
})
    .superRefine((value, ctx) => {
    requireContactIdsOrSelection(value, ctx);
    if (value.mode === "clear") {
        return;
    }
    const groupCount = (value.groupIds?.length ?? 0) + (value.newGroupNames?.length ?? 0);
    if (value.mode === "remove") {
        if ((value.groupIds?.length ?? 0) === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "groupIds required for remove mode",
                path: ["groupIds"],
            });
        }
        return;
    }
    if (groupCount === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At least one group is required",
            path: ["groupIds"],
        });
    }
});
export const contactBulkGroupsResponseSchema = z.object({
    updated: z.number().int().nonnegative(),
});
/** Re-export for contact detail drawer compatibility with inbox DTOs */
export { contactPlatformProfileSummaryDtoSchema };
//# sourceMappingURL=contact-contracts.js.map